import { useEffect } from 'react';

/**
 * JsonLd — Injeta JSON-LD estruturado no <head> para SEO.
 *
 * Tipos suportados:
 *   - "Store"       → dados da loja/empresa
 *   - "Product"     → página de produto
 *   - "BreadcrumbList" → breadcrumbs
 *   - "WebSite"     → SearchAction (sitelinks search box)
 *
 * Props:
 *   type (string) — obrigatório
 *   data (object) — dados específicos do tipo
 *   id   (string) — identificador do script (evita duplicatas)
 */
export default function JsonLd({ type, data, id }) {
  useEffect(() => {
    if (!type || !data) return;

    const scriptId = id || `jsonld-${type.toLowerCase()}`;
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }

    const schema = buildSchema(type, data);
    if (schema) {
      script.textContent = JSON.stringify(schema);
    }

    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, [type, data, id]);

  return null;
}

function buildSchema(type, data) {
  switch (type) {
    case 'Store':
      return buildStore(data);
    case 'Product':
      return buildProduct(data);
    case 'BreadcrumbList':
      return buildBreadcrumbs(data);
    case 'WebSite':
      return buildWebSite(data);
    default:
      return null;
  }
}

/**
 * Store/Organization — dados do tenant
 * data: { nome, descricao_site, url (slug), sac_telefone, sac_email, logo (url completa) }
 */
function buildStore(data) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Store',
    name: data.nome || '',
    url: data.siteUrl || window.location.origin,
    description: data.descricao_site || '',
  };

  if (data.logo) {
    schema.logo = data.logo;
    schema.image = data.logo;
  }

  if (data.sac_telefone || data.sac_email) {
    schema.contactPoint = {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      ...(data.sac_telefone && { telephone: data.sac_telefone }),
      ...(data.sac_email && { email: data.sac_email }),
    };
  }

  return schema;
}

/**
 * Product — página de produto
 * data: { nome, descricao, imagem, preco, precoOriginal, disponivel, url, marca, moeda,
 *   keywords (string[]), principiosAtivos (string[]) }
 */
function buildProduct(data) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: data.nome || '',
    description: data.descricao || data.nome || '',
    image: data.imagem || '',
    url: data.url || window.location.href,
  };

  if (data.marca) {
    schema.brand = { '@type': 'Brand', name: data.marca };
  }

  if (data.keywords && data.keywords.length > 0) {
    schema.keywords = data.keywords.join(', ');
  }

  if (data.principiosAtivos && data.principiosAtivos.length > 0) {
    schema.additionalProperty = data.principiosAtivos.map(nome => ({
      '@type': 'PropertyValue',
      name: 'Princípio ativo',
      value: nome,
    }));
  }

  if (data.preco != null && data.preco > 0) {
    schema.offers = {
      '@type': 'Offer',
      price: data.preco,
      priceCurrency: data.moeda || 'BRL',
      availability: data.disponivel
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      url: data.url || window.location.href,
    };
  }

  return schema;
}

/**
 * BreadcrumbList
 * data: { items: [{ name, url }] }
 */
function buildBreadcrumbs(data) {
  if (!data.items || data.items.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: data.items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * WebSite — sitelinks search box
 * data: { nome, siteUrl, searchUrlTemplate }
 */
function buildWebSite(data) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: data.nome || '',
    url: data.siteUrl || window.location.origin,
  };

  if (data.searchUrlTemplate) {
    schema.potentialAction = {
      '@type': 'SearchAction',
      target: data.searchUrlTemplate,
      'query-input': 'required name=search_term_string',
    };
  }

  return schema;
}
